<script setup>
import { onMounted, computed, ref, reactive, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { debounce } from 'shared/utils/index.js';
import { useUISettings } from 'dashboard/composables/useUISettings';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

import ContactsListLayout from 'dashboard/components-next/Contacts/ContactsListLayout.vue';
import ContactsList from 'dashboard/components-next/Contacts/Pages/ContactsList.vue';
import ContactEmptyState from 'dashboard/components-next/Contacts/EmptyState/ContactEmptyState.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SectionLayout from 'dashboard/components-next/SectionLayout.vue';
import ContactListHeaderWrapper from 'dashboard/components-next/Contacts/ContactsHeader/ContactListHeaderWrapper.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Modal from 'dashboard/components/Modal.vue';
import ContactAPI from 'dashboard/api/contacts';
import { useAlert } from 'dashboard/composables';

const DEFAULT_SORT_FIELD = 'last_activity_at';
const DEBOUNCE_DELAY = 300;
const ITEMS_PER_PAGE = 15;

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const alert = useAlert;

const { updateUISettings, uiSettings } = useUISettings();

const contacts = useMapGetter('contacts/getContactsList');
const uiFlags = useMapGetter('contacts/getUIFlags');
const customViewsUiFlags = useMapGetter('customViews/getUIFlags');
const segments = useMapGetter('customViews/getContactCustomViews');
const appliedFilters = useMapGetter('contacts/getAppliedContactFilters');
const meta = useMapGetter('contacts/getMeta');
const isFeatureEnabledOnAccount = useMapGetter('accounts/isFeatureEnabledonAccount');
const inboxes = useMapGetter('inboxes/getInboxes');

const accountId = computed(() => Number(route.params.accountId));

const searchQuery = computed(() => route.query?.search);
const searchValue = ref(searchQuery.value || '');
const composeRef = ref(null);
const selectedComposeContactId = ref(null);
const pageNumber = computed(() => Number(route.query?.page) || 1);

const currentPage = ref(1);
const hasMore = computed(() => {
  const totalPages = Math.ceil((meta.value?.count || 0) / ITEMS_PER_PAGE);
  return currentPage.value < totalPages;
});

const isFetchingList = computed(
  () => uiFlags.value.isFetching || customViewsUiFlags.value.isFetching
);

const isLoadingMore = computed(() => uiFlags.value.isFetchingMore);


const parseSortSettings = (sortString = '') => {
  const hasDescending = sortString.startsWith('-');
  const sortField = hasDescending ? sortString.slice(1) : sortString;
  return {
    sort: sortField || DEFAULT_SORT_FIELD,
    order: hasDescending ? '-' : '',
  };
};

const { contacts_sort_by: contactSortBy = '' } = uiSettings.value ?? {};
const { sort: initialSort, order: initialOrder } =
  parseSortSettings(contactSortBy);

const sortState = reactive({
  activeSort: initialSort,
  activeOrdering: initialOrder,
});

const activeLabel = computed(() => route.params.label);
const activeSegmentId = computed(() => route.params.segmentId);
const activeInboxId = computed(() => route.params.inboxId);
const totalItems = computed(() => meta.value?.count);
const activeSegment = computed(() => {
  if (!activeSegmentId.value) return undefined;
  return segments.value.find(view => view.id === Number(activeSegmentId.value));
});

const hasContacts = computed(() => contacts.value.length > 0);
const isContactIndexView = computed(
  () => route.name === 'contacts_dashboard_index' && pageNumber.value === 1
);
const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length > 0;
});
const showEmptyStateLayout = computed(() => {
  return (
    !searchQuery.value &&
    !hasContacts.value &&
    isContactIndexView.value &&
    !hasAppliedFilters.value
  );
});
const showEmptyText = computed(() => {
  return (
    (searchQuery.value ||
      hasAppliedFilters.value ||
      !isContactIndexView.value) &&
    !hasContacts.value
  );
});

const showDiscoveryModal = ref(false);
const discoveryPhone = ref('');
const isDiscovering = ref(false);
const discoveryResult = ref(null);
const discoveryError = ref('');
const showChannelPicker = ref(false);

const zaloPersonalInboxes = computed(() => {
  return (inboxes.value || []).filter(
    inbox => inbox.channel_type === INBOX_TYPES.ZALO_PERSONAL
  );
});

const hasZaloPersonalInbox = computed(
  () => zaloPersonalInboxes.value && zaloPersonalInboxes.value.length > 0
);

const isContactDiscoveryEnabled = computed(() => {
  if (!accountId.value || !hasZaloPersonalInbox.value) return false;
  return isFeatureEnabledOnAccount.value(
    accountId.value,
    FEATURE_FLAGS.CONTACT_DISCOVERY
  );
});

const headerTitle = computed(() => {
  if (searchQuery.value) return t('CONTACTS_LAYOUT.HEADER.SEARCH_TITLE');
  if (activeSegmentId.value) return activeSegment.value?.name;
  if (activeLabel.value) return `#${activeLabel.value}`;
  return t('CONTACTS_LAYOUT.HEADER.TITLE');
});

const isNotSegmentView = computed(() => {
  return route.name !== 'contacts_dashboard_segments_index';
});
const isLabelView = computed(
  () => route.name === 'contacts_dashboard_labels_index'
);

const buildSortAttr = () =>
  `${sortState.activeOrdering}${sortState.activeSort}`;

const getCommonFetchParams = (page = 1) => ({
  page,
  sortAttr: buildSortAttr(),
  label: activeLabel.value,
  inboxId: activeInboxId.value,
  perPage: ITEMS_PER_PAGE,
});

const fetchContacts = async (page = 1) => {
  await store.dispatch('contacts/clearContactFilters');
  await store.dispatch('contacts/get', getCommonFetchParams(page));
  currentPage.value = page;
};

const fetchMoreContacts = async (page = 1) => {
  await store.dispatch('contacts/getMore', getCommonFetchParams(page));
  currentPage.value = page;
};

const fetchSavedOrAppliedFilteredContact = async (payload, page = 1) => {
  if (!activeSegmentId.value && !hasAppliedFilters.value) return;
  await store.dispatch('contacts/filter', {
    ...getCommonFetchParams(page),
    queryPayload: payload,
  });
  currentPage.value = page;
};

const searchContacts = debounce(async (value, page = 1) => {
  await store.dispatch('contacts/clearContactFilters');
  searchValue.value = value;

  if (!value) {
    await fetchContacts(page);
    return;
  }

  await store.dispatch('contacts/search', {
    ...getCommonFetchParams(page),
    search: encodeURIComponent(value),
  });
  currentPage.value = page;
}, DEBOUNCE_DELAY);

const fetchContactsBasedOnContext = async page => {
  if (isFetchingList.value) return;
  if (searchQuery.value) {
    await searchContacts(searchQuery.value, page);
    return;
  }
  // Reset the search value when we change the view
  searchValue.value = '';
  // If there are applied filters or active segment with query
  if (
    (hasAppliedFilters.value || activeSegment.value?.query) &&
    !activeLabel.value
  ) {
    const queryPayload =
      activeSegment.value?.query || filterQueryGenerator(appliedFilters.value);
    await fetchSavedOrAppliedFilteredContact(queryPayload, page);
    return;
  }
  // Default case: fetch regular contacts + label
  await fetchContacts(page);
};

const handleSort = async ({ sort, order }) => {
  Object.assign(sortState, { activeSort: sort, activeOrdering: order });

  await updateUISettings({
    contacts_sort_by: buildSortAttr(),
  });

  if (searchQuery.value) {
    await searchContacts(searchValue.value);
    return;
  }

  await (activeSegmentId.value || hasAppliedFilters.value
    ? fetchSavedOrAppliedFilteredContact(
        activeSegmentId.value
          ? activeSegment.value?.query
          : filterQueryGenerator(appliedFilters.value)
      )
    : fetchContacts());
};

const openComposeForContact = (contactId) => {
  selectedComposeContactId.value = String(contactId);
  // Ensure ref exists before calling
  if (composeRef.value && typeof composeRef.value.open === 'function') {
    composeRef.value.open();
  }
};

const createContact = async contact => {
  await store.dispatch('contacts/create', contact);
};

const openDiscoveryModal = (initialPhone = '') => {
  discoveryPhone.value = initialPhone || searchValue.value || '';
  discoveryResult.value = null;
  discoveryError.value = '';
  showDiscoveryModal.value = true;
};

const closeDiscoveryModal = () => {
  showDiscoveryModal.value = false;
  showChannelPicker.value = false;
};

const runDiscovery = async () => {
  const phone = discoveryPhone.value?.trim();
  if (!phone) return;

  isDiscovering.value = true;
  discoveryError.value = '';
  discoveryResult.value = null;

  try {
    const {
      data: { payload },
    } = await ContactAPI.discover(phone);

    discoveryResult.value = payload || null;

    // Refresh contacts list so the new/updated contact appears
    await fetchContactsBasedOnContext(currentPage.value);
  } catch (error) {
    const apiError =
      error?.response?.data?.error ||
      t('CONTACTS_LAYOUT.DISCOVERY.ERROR_MESSAGE');
    discoveryError.value = apiError;
  } finally {
    isDiscovering.value = false;
  }
};

const contactAvatar = computed(() => {
  if (!discoveryResult.value || !discoveryResult.value.contact) return '';
  const contact = discoveryResult.value.contact;
  return contact.avatar || contact.thumbnail || '';
});

const toggleChannelPicker = () => {
  showChannelPicker.value = !showChannelPicker.value;
};

const applyDiscoveryForInbox = async inboxId => {
  const phone = discoveryPhone.value?.trim();
  if (!phone || !inboxId) return;

  isDiscovering.value = true;
  discoveryError.value = '';

  try {
    const {
      data: { payload },
    } = await ContactAPI.applyDiscovery(phone, inboxId);
    const appliedContact = payload || null;

    // Sau khi lưu vào CRM, coi như contact đã tồn tại
    if (discoveryResult.value) {
      discoveryResult.value.existing_contact = true;
      if (appliedContact) {
        discoveryResult.value.contact = {
          ...(discoveryResult.value.contact || {}),
          id: appliedContact.id,
          name: appliedContact.name,
          phone_number: appliedContact.phone_number,
          email: appliedContact.email,
          thumbnail: appliedContact.thumbnail,
        };
      }
    }
    showChannelPicker.value = false;
    await fetchContactsBasedOnContext(currentPage.value);
    alert(
      t('CONTACTS_LAYOUT.DISCOVERY.ADD_TO_CRM'),
      { type: 'success' }
    );
  } catch (error) {
    const apiError =
      error?.response?.data?.error ||
      t('CONTACTS_LAYOUT.DISCOVERY.ERROR_MESSAGE');
    discoveryError.value = apiError;
  } finally {
    isDiscovering.value = false;
  }
};

const openContactFromDiscovery = () => {
  const contactId = discoveryResult.value?.contact?.id;
  if (!contactId || !accountId.value) return;

  router.push({
    name: 'contacts_edit',
    params: { accountId: accountId.value, contactId },
  });
  closeDiscoveryModal();
};


const handleLoadMore = async () => {
  if (isFetchingList.value || isLoadingMore.value || !hasMore.value) return;
  
  const nextPage = currentPage.value + 1;
  
  try {
    if (searchQuery.value) {
      await searchContacts(searchValue.value, nextPage);
    } else if (activeSegmentId.value || hasAppliedFilters.value) {
      const queryPayload = activeSegmentId.value
        ? activeSegment.value?.query
        : filterQueryGenerator(appliedFilters.value);
      await fetchSavedOrAppliedFilteredContact(queryPayload, nextPage);
    } else {
      await fetchMoreContacts(nextPage);
    }
  } finally {
    // Vuex sẽ tự động update loading state
  }
};

// Handle page change for table view
const handlePageChange = async (pageNumber) => {
  await fetchContactsBasedOnContext(pageNumber);
};

watch(
  () => uiSettings.value?.contacts_sort_by,
  newSortBy => {
    if (newSortBy) {
      const { sort, order } = parseSortSettings(newSortBy);
      sortState.activeSort = sort;
      sortState.activeOrdering = order;
    }
  },
  { immediate: true }
);

watch(
  [activeLabel, activeSegment, activeInboxId],
  () => {
    fetchContactsBasedOnContext(pageNumber.value);
  },
  { deep: true }
);

watch(searchQuery, value => {
  if (isFetchingList.value) return;
  searchValue.value = value || '';
  // Reset the view if there is search query when we click on the sidebar group
  if (value === undefined) {
    fetchContacts();
  }
});

onMounted(async () => {
  if (!activeSegmentId.value) {
    if (searchQuery.value) {
      await searchContacts(searchQuery.value, pageNumber.value);
      return;
    }
    await fetchContacts(pageNumber.value);
  } else if (activeSegment.value && activeSegmentId.value) {
    await fetchSavedOrAppliedFilteredContact(
      activeSegment.value.query,
      pageNumber.value
    );
  }
});
</script>

<template>
  <SectionLayout :title="headerTitle">
    <template #header-left>
      <div class="hidden xl:block">
        <ContactListHeaderWrapper
        :show-search="isNotSegmentView"
        :search-value="searchValue"
        :active-sort="sortState.activeSort"
        :active-ordering="sortState.activeOrdering"
        :active-segment="activeSegment"
        :segments-id="activeSegmentId"
        :has-applied-filters="hasAppliedFilters"
        :is-label-view="isLabelView"
        @search="searchContacts"
        @update:sort="handleSort"
        @apply-filter="fetchSavedOrAppliedFilteredContact"
        @clear-filters="fetchContacts"
      />
      </div>
      <!-- Send Message button for mobile/tablet/lg -->
      <div class="flex xl:hidden items-center gap-2">
        <ComposeConversation>
          <template #trigger="{ toggle }">
            <Button
              class="!bg-grass-400 font-medium !h-8 px-3 text-sm"
              :label="$t('CONTACTS_LAYOUT.HEADER.MESSAGE_BUTTON')"
              size="sm"
              icon="i-lucide-send"
              @click="toggle"
            />
          </template>
        </ComposeConversation>
        <Button
          v-if="isContactDiscoveryEnabled"
          class="font-medium !h-8 px-3 text-sm"
          variant="clear"
          icon="i-lucide-search"
          :label="$t('CONTACTS_LAYOUT.HEADER.DISCOVER_BUTTON')"
          @click="openDiscoveryModal()"
        />
      </div>
      <!-- Send Message button for desktop (xl) -->
      <div class="hidden xl:flex items-center gap-2">
        <ComposeConversation ref="composeRef" :contact-id="selectedComposeContactId" popover-class="ltr:right-3 rtl:left-3">
          <template #trigger="{ toggle }">
            <Button
              class="!bg-grass-400 font-medium !h-10 p-[10px]"
              :label="$t('CONTACTS_LAYOUT.HEADER.MESSAGE_BUTTON')"
              size="sm"
              icon="i-lucide-send"
              trailing-icon
              @click="toggle"
            />
          </template>
        </ComposeConversation>
        <Button
          v-if="isContactDiscoveryEnabled"
          class="font-medium !h-10 p-[10px]"
          variant="clear"
          icon="i-lucide-search"
          :label="$t('CONTACTS_LAYOUT.HEADER.DISCOVER_BUTTON')"
          @click="openDiscoveryModal()"
        />
      </div>
    </template>
    <div class="block xl:hidden">
      <ContactListHeaderWrapper 
        :show-search="isNotSegmentView"
        :search-value="searchValue"
        :active-sort="sortState.activeSort"
        :active-ordering="sortState.activeOrdering"
        :active-segment="activeSegment"
        :segments-id="activeSegmentId"
        :has-applied-filters="hasAppliedFilters"
        :is-label-view="isLabelView"
        @search="searchContacts"
        @update:sort="handleSort"
        @apply-filter="fetchSavedOrAppliedFilteredContact"
        @clear-filters="fetchContacts"
      />
    </div>

    <ContactsListLayout
      :current-page="currentPage"
      :total-items="totalItems"
      :items-per-page="ITEMS_PER_PAGE"
      :show-pagination-footer="!isFetchingList && hasContacts"
      :active-segment="activeSegment"
      :has-applied-filters="hasAppliedFilters"
      :is-fetching-list="isFetchingList"
      @update:current-page="fetchContactsBasedOnContext"
      @clear-filters="fetchContacts"
    >
      <div
        v-if="isFetchingList && !isLoadingMore"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>

      <template v-else>
        <ContactEmptyState
          v-if="showEmptyStateLayout"
          class="pt-14"
          :title="t('CONTACTS_LAYOUT.EMPTY_STATE.TITLE')"
          :subtitle="t('CONTACTS_LAYOUT.EMPTY_STATE.SUBTITLE')"
          :button-label="t('CONTACTS_LAYOUT.EMPTY_STATE.BUTTON_LABEL')"
          @create="createContact"
        />

        <div
          v-else-if="showEmptyText"
          class="flex items-center justify-center py-10"
        >
          <span class="text-base text-n-slate-11">
            {{
              searchQuery || !hasAppliedFilters
                ? t('CONTACTS_LAYOUT.EMPTY_STATE.SEARCH_EMPTY_STATE_TITLE')
                : t('CONTACTS_LAYOUT.EMPTY_STATE.LIST_EMPTY_STATE_TITLE')
            }}
          </span>
        </div>

        <ContactsList 
          v-else 
          :contacts="contacts"
          :total-count="totalItems"
          :current-page="currentPage"
          :page-size="ITEMS_PER_PAGE"
          :is-loading="isLoadingMore"
          :has-more="hasMore"
          @sendMessage="openComposeForContact"
          @pageChange="handlePageChange"
          @loadMore="handleLoadMore"
        />
      </template>
    </ContactsListLayout>

    <Modal
      v-if="isContactDiscoveryEnabled"
      v-model:show="showDiscoveryModal"
      :on-close="closeDiscoveryModal"
      modal-type="centered"
      size="medium"
    >
      <div class="content flex flex-col gap-4">
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.DISCOVERY.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('CONTACTS_LAYOUT.DISCOVERY.DESCRIPTION') }}
        </p>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.DISCOVERY.INPUT_LABEL') }}
          </label>
          <input
            v-model="discoveryPhone"
            type="tel"
            class="w-full border border-n-strong rounded-md px-3 py-2 text-sm bg-transparent outline-none focus:ring-1 focus:ring-n-strong"
            :placeholder="t('CONTACTS_LAYOUT.DISCOVERY.INPUT_PLACEHOLDER')"
          />
        </div>

        <div class="flex justify-end gap-2 mt-2">
          <Button
            variant="clear"
            size="sm"
            :label="t('GENERAL.CANCEL')"
            @click="closeDiscoveryModal"
          />
          <Button
            size="sm"
            class="!bg-grass-400 font-medium"
            :label="
              isDiscovering
                ? t('CONTACTS_LAYOUT.DISCOVERY.LOADING')
                : t('CONTACTS_LAYOUT.DISCOVERY.SEARCH_BUTTON')
            "
            :disabled="isDiscovering || !discoveryPhone"
            @click="runDiscovery"
          />
        </div>

        <div v-if="discoveryError" class="text-sm text-n-ruby-9">
          {{ discoveryError }}
        </div>

        <div
          v-if="discoveryResult && discoveryResult.contact"
          class="mt-2 border-t border-n-strong pt-3 flex flex-col gap-3"
        >
          <div class="flex items-center justify-between gap-4">
            <div class="flex items-center gap-3">
              <img
                v-if="contactAvatar"
                :src="contactAvatar"
                alt=""
                class="w-10 h-10 rounded-full object-cover"
              />
              <div class="flex flex-col gap-1">
                <span class="text-sm font-semibold text-n-slate-12">
                  {{
                    discoveryResult.contact.name ||
                    discoveryResult.contact.phone_number ||
                    discoveryResult.contact.email
                  }}
                </span>
                <span class="text-xs text-n-slate-11">
                  <span v-if="discoveryResult.contact.phone_number">
                    {{ discoveryResult.contact.phone_number }}
                  </span>
                  <span v-if="discoveryResult.contact.email">
                    <template v-if="discoveryResult.contact.phone_number">
                      ·
                    </template>
                    {{ discoveryResult.contact.email }}
                  </span>
                </span>
                <span class="text-xs text-n-slate-11">
                  {{
                    discoveryResult.existing_contact
                      ? t('CONTACTS_LAYOUT.DISCOVERY.RESULT_EXISTING')
                      : t('CONTACTS_LAYOUT.DISCOVERY.RESULT_NEW')
                  }}
                </span>
              </div>
            </div>

            <div class="flex items-center gap-2">
              <Button
                v-if="!discoveryResult.existing_contact && zaloPersonalInboxes.length"
                variant="clear"
                size="sm"
                :label="t('CONTACTS_LAYOUT.DISCOVERY.ADD_TO_CRM')"
                icon="i-lucide-user-plus"
                @click="toggleChannelPicker"
              />
              <Button
                v-if="discoveryResult.existing_contact && discoveryResult.contact && discoveryResult.contact.id"
                variant="clear"
                size="sm"
                :label="t('CONTACTS_LAYOUT.DISCOVERY.OPEN_CONTACT')"
                @click="openContactFromDiscovery"
              />
            </div>
          </div>

          <div
            v-if="showChannelPicker && zaloPersonalInboxes.length"
            class="flex flex-col gap-2 mt-2"
          >
            <span class="text-xs text-n-slate-11">
              {{ t('CONTACTS_LAYOUT.DISCOVERY.SELECT_CHANNEL') }}
            </span>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="inbox in zaloPersonalInboxes"
                :key="inbox.id"
                class="inline-flex items-center px-3 py-1.5 rounded-full text-xs bg-n-alpha-2 text-n-slate-11 border border-n-strong cursor-pointer hover:bg-n-alpha-3"
                @click="applyDiscoveryForInbox(inbox.id)"
              >
                {{ inbox.name }}
              </span>
            </div>
          </div>

          <div v-if="discoveryResult.channels && discoveryResult.channels.length" class="flex flex-wrap gap-2">
            <span
              v-for="channel in discoveryResult.channels"
              :key="`${channel.channel_type}-${channel.inbox_id}`"
              class="inline-flex items-center px-2 py-1 rounded-full text-xs bg-n-alpha-2 text-n-slate-11"
            >
              {{ channel.channel_type }}
            </span>
          </div>
        </div>
      </div>
    </Modal>
  </SectionLayout>
</template>
